import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, XCircle, MapPin, Warning, SignIn, Clock } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

import { devWarn } from "@/lib/devLog";
import { fmtDateOpts, fmtTimeOpts } from "@/lib/helpers";
/**
 * Public / semi-public QR check-in landing page.
 *
 * URL: /qr/checkin?b=<branch_id>&s=<qr_secret>
 *
 * Flow:
 *   1. Validate the QR by hitting the public `/api/qr/checkin/info` — no
 *      login required. If the QR is invalid or the workspace's add-on
 *      has lapsed, we show a friendly stop screen with a Close button.
 *   2. If not logged in, offer a "Sign in to check in" button that
 *      redirects to /login?next=<current url>. The user is bounced back
 *      here after login and the flow continues.
 *   3. Ask for browser geolocation if the branch mandates GPS. Show
 *      accuracy + geofence status so it's clear when a scan is off-site.
 *   4. POST to `/api/qr/checkin/submit` — the server does the second
 *      round of signature/geofence checks and stamps
 *      `attendance.source='qr'`. Result is rendered as a full-screen
 *      outcome card (success OR error) with a big Close/Done button
 *      that either closes the tab (native-scanner case) or navigates
 *      back into the app (in-app scan case).
 */
export default function QRCheckinPage() {
  const [params] = useSearchParams();
  const b = params.get("b");
  const s = params.get("s");
  const navigate = useNavigate();
  const { user } = useAuth();

  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [gps, setGps] = useState(null);   // { lat, lng, accuracy_m } | null
  const [gpsErr, setGpsErr] = useState(null);
  const [busy, setBusy] = useState(false);
  // outcome:
  //   null                                       — no submission yet
  //   { kind: "success", action, time, ... }     — accepted by server
  //   { kind: "error",   message }               — rejected / failed
  const [outcome, setOutcome] = useState(null);
  const [showCloseHint, setShowCloseHint] = useState(false);

  // --- Validate the QR (public). -------------------------------------
  useEffect(() => {
    if (!b || !s) {
      setInfo({ valid: false, reason: "Missing QR parameters." });
      setLoadingInfo(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/qr/checkin/info?b=${encodeURIComponent(b)}&s=${encodeURIComponent(s)}`);
        setInfo(data);
      } catch (e) {
        setInfo({ valid: false, reason: formatApiError(e) });
      } finally {
        setLoadingInfo(false);
      }
    })();
  }, [b, s]);

  // --- GPS request when branch mandates it. --------------------------
  useEffect(() => {
    if (!info?.valid || !info.gps_required || !user) return;
    if (!("geolocation" in navigator)) {
      setGpsErr("This device does not expose GPS. Ask your admin to switch to manual attendance.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
        });
        setGpsErr(null);
      },
      (err) => setGpsErr(err.message || "Location permission denied. Enable location access and reload."),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, [info, user]);

  const submit = async (action) => {
    if (!info?.valid) return;
    setBusy(true);
    setOutcome(null);
    setShowCloseHint(false);
    try {
      const body = { branch_id: b, secret: s, action };
      if (gps) {
        body.gps_lat = gps.lat;
        body.gps_lng = gps.lng;
        body.accuracy_m = gps.accuracy_m;
      }
      const { data } = await api.post("/qr/checkin/submit", body);
      // Success. Extract the timestamp we care about — the attendance
      // row echoes back with time_in / time_out ISO strings.
      const ts = action === "in"
        ? (data.attendance?.time_in || new Date().toISOString())
        : (data.attendance?.time_out || new Date().toISOString());
      setOutcome({
        kind: "success",
        action,
        time: ts,
        already: action === "in" ? !!data.already_checked_in : !!data.already_checked_out,
        outside_geofence: !!data.outside_geofence,
        distance_m: data.distance_m,
        attendance: data.attendance,
      });
    } catch (e) {
      // Surface a user-friendly reason for the common cases. The server
      // already sends a clear detail for most: invalid QR, wrong workspace,
      // clerk/parent block, GPS-required, already-checked-in, etc. We
      // keep the raw message as-is so translations stay in one place.
      setOutcome({ kind: "error", message: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const goLogin = () => {
    const next = `${window.location.pathname}${window.location.search}`;
    navigate(`/login?next=${encodeURIComponent(next)}`);
  };

  // --- Close / Done handler ------------------------------------------
  //
  // Three routes the user could have arrived here from:
  //   (a) Tapped a QR opened by an in-app SPA navigation (scan panel
  //       does `window.location.href = ...`). We have browser history
  //       and a same-origin referrer.
  //   (b) Opened as a script-opened popup (`window.open` from another
  //       page in the app) — `window.opener` is set.
  //   (c) Opened as a fresh tab by a native camera app or mobile QR
  //       reader. No opener, no history, no in-app referrer.
  //
  // (a) → `navigate(-1)` returns to the previous in-app screen.
  // (b) → `window.close()` closes the popup cleanly.
  // (c) → `window.close()` is silently blocked; we surface a manual
  //       "Close this tab to return to the app" instruction so the user
  //       is never stranded.
  const closeOrReturn = useCallback(() => {
    const hasInAppHistory =
      typeof window !== "undefined" &&
      window.history.length > 1 &&
      document.referrer &&
      document.referrer.startsWith(window.location.origin);

    if (hasInAppHistory) {
      navigate(-1);
      return;
    }
    // Script-opened popup: window.opener is same-origin.
    if (typeof window !== "undefined" && window.opener && !window.opener.closed) {
      try {
        window.close();
      } catch (e) {
        // Some mobile browsers refuse window.close() when we didn't
        // open the tab ourselves. We fall back to the hint below.
        devWarn("[QRCheckin] window.close() blocked by browser:", e);
      }
      // If we're still visible after a tick (mobile browsers often
      // refuse close() from a tab they didn't spawn), show the hint.
      setTimeout(() => setShowCloseHint(true), 250);
      return;
    }
    // Fresh tab — try close, else instruct the user.
    try {
      window.close();
    } catch (e) {
      devWarn("[QRCheckin] window.close() blocked by browser:", e);
    }
    setTimeout(() => setShowCloseHint(true), 250);
  }, [navigate]);

  // ---------------------- RENDER ----------------------

  if (loadingInfo) {
    return <Frame><div className="p-8 text-center text-muted-foreground">Checking QR…</div></Frame>;
  }

  if (!info?.valid) {
    return (
      <Frame>
        <OutcomePanel
          kind="error"
          title="QR not valid"
          message={info?.reason || "Please try again."}
          onClose={closeOrReturn}
          showCloseHint={showCloseHint}
        />
      </Frame>
    );
  }

  if (!user) {
    return (
      <Frame>
        <div className="text-center py-6" data-testid="qr-need-login">
          <SignIn size={40} weight="duotone" className="mx-auto text-primary" />
          <div className="mt-2 font-semibold">Sign in to check in</div>
          <div className="text-xs text-muted-foreground mb-4">
            Branch: <span className="font-medium text-foreground">{info.branch_name}</span>
          </div>
          <Button className="w-full h-12" onClick={goLogin} data-testid="qr-login-btn">
            Sign in
          </Button>
        </div>
      </Frame>
    );
  }

  if (outcome?.kind === "success") {
    return (
      <Frame>
        <OutcomePanel
          kind="success"
          title={successTitle(outcome)}
          message={
            <>
              <div className="text-xs text-muted-foreground">
                {outcome.action === "in" ? "Check-in time" : "Check-out time"}
              </div>
              <div className="mt-0.5 text-2xl font-semibold text-foreground" data-testid="qr-outcome-time">
                {formatTime(outcome.time)}
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">
                {formatDate(outcome.time)} · {info.branch_name}
              </div>
              {outcome.outside_geofence && (
                <div className="mt-3 inline-flex items-center gap-1 text-amber-700 text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="qr-outside-geofence">
                  <Warning size={14} /> You are {Math.round(outcome.distance_m || 0)}m from the branch — flagged for admin review.
                </div>
              )}
            </>
          }
          onClose={closeOrReturn}
          showCloseHint={showCloseHint}
        />
      </Frame>
    );
  }

  if (outcome?.kind === "error") {
    return (
      <Frame>
        <OutcomePanel
          kind="error"
          title="Check-in failed"
          message={outcome.message}
          onClose={closeOrReturn}
          showCloseHint={showCloseHint}
          onRetry={() => setOutcome(null)}
        />
      </Frame>
    );
  }

  return (
    <Frame>
      <div className="space-y-4" data-testid="qr-ready">
        <div className="text-center">
          <div className="text-xs text-muted-foreground">Checking in as</div>
          <div className="font-semibold">{user.name}</div>
          <div className="text-xs text-muted-foreground">Branch: {info.branch_name}</div>
        </div>

        {info.gps_required && (
          <div className="rounded border p-3 bg-muted/30" data-testid="qr-gps-block">
            <div className="flex items-center gap-2 text-sm font-medium">
              <MapPin size={16} className="text-primary" /> GPS required
            </div>
            {gps ? (
              <div className="mt-1 text-xs text-muted-foreground">
                Location acquired — accuracy ±{Math.round(gps.accuracy_m || 0)}m.
              </div>
            ) : gpsErr ? (
              <div className="mt-1 text-xs text-destructive" data-testid="qr-gps-error">{gpsErr}</div>
            ) : (
              <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                <Clock size={12} /> Waiting for location…
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="default"
            className="h-14 text-base"
            disabled={busy || (info.gps_required && !gps)}
            onClick={() => submit("in")}
            data-testid="qr-checkin-in-btn"
          >
            {busy ? "…" : "Check in"}
          </Button>
          <Button
            variant="outline"
            className="h-14 text-base"
            disabled={busy || (info.gps_required && !gps)}
            onClick={() => submit("out")}
            data-testid="qr-checkin-out-btn"
          >
            {busy ? "…" : "Check out"}
          </Button>
        </div>

        <Button
          variant="ghost"
          size="sm"
          className="w-full text-muted-foreground"
          onClick={closeOrReturn}
          data-testid="qr-cancel-btn"
        >
          Cancel
        </Button>
      </div>
    </Frame>
  );
}

// -----------------------------------------------------------------------

/**
 * Reusable outcome card used for both success and error states — keeps
 * the button size, layout and testids consistent. The Close button is
 * always immediately below the message so a mobile user never needs to
 * scroll to reach it.
 */
function OutcomePanel({ kind, title, message, onClose, showCloseHint, onRetry }) {
  const isSuccess = kind === "success";
  const Icon = isSuccess ? CheckCircle : XCircle;
  const iconColor = isSuccess ? "text-emerald-500" : "text-rose-500";

  return (
    <div className="text-center py-4 space-y-4" data-testid={`qr-outcome-${kind}`}>
      <Icon size={56} weight="fill" className={`mx-auto ${iconColor}`} />
      <div>
        <div className="text-lg font-semibold" data-testid="qr-outcome-title">{title}</div>
        <div className="mt-1 text-sm text-muted-foreground">{message}</div>
      </div>

      {/* Close/Done — LARGE, always visible above the fold on a phone. */}
      <div className="pt-2 space-y-2">
        <Button
          className="w-full h-14 text-base"
          onClick={onClose}
          data-testid="qr-close-btn"
        >
          Done
        </Button>
        {onRetry && (
          <Button
            variant="outline"
            className="w-full h-11"
            onClick={onRetry}
            data-testid="qr-retry-btn"
          >
            Try again
          </Button>
        )}
      </div>

      {showCloseHint && (
        <div
          className="mt-1 rounded border border-primary/25 bg-primary/5 text-xs text-primary p-3"
          data-testid="qr-close-hint"
        >
          Please close this tab to return to the TherapyFlow app.
        </div>
      )}
    </div>
  );
}

function Frame({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background flex items-center justify-center p-4">
      <Card className="w-full max-w-sm shadow-lg" data-testid="qr-checkin-page">
        <CardHeader className="pb-2">
          <CardTitle className="text-center text-lg">TherapyFlow Check-in</CardTitle>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}

// --------- Helpers ---------

function successTitle(o) {
  if (o.action === "in")  return o.already ? "Already checked in" : "Checked in";
  if (o.action === "out") return o.already ? "Already checked out" : "Checked out";
  return "Done";
}

function formatTime(iso) {
  try {
    return fmtTimeOpts(iso, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return iso || "";
  }
}
function formatDate(iso) {
  try {
    return fmtDateOpts(iso, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso || "";
  }
}
